import { CountryTag, useCountries } from "@/components/Country";
import { SalesGoal } from "@/components/SalesGoal";
import { MissingShorts } from "@/components/MissingShorts";
import { PriceRefresh } from "@/components/PriceRefresh";
import { MarketSnapshot } from "@/components/MarketSnapshot";
import { UpdateAllGuides } from "@/components/UpdateAllGuides";
import { AssetHealth } from "@/components/AssetHealth";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { ReminderSettings } from "@/components/ReminderSettings";

const Stat = ({ label, value, testId, sub }) => (
  <div className="border-t-2 border-[#3B1B48] pt-4">
    <p className="eyebrow">{label}</p>
    <p data-testid={testId} className="font-display text-5xl text-[#3B1B48] mt-2">{value ?? "—"}</p>
    {sub && <p data-testid={`${testId}-sub`} className="text-xs text-[#8C827A] mt-1">{sub}</p>}
  </div>
);

export default function Dashboard() {
  const [guides, setGuides] = useState(null);
  const [stats, setStats] = useState({});
  const [sales, setSales] = useState({});
  const [country, setCountry] = useState("");
  const countryList = useCountries();
  const countOf = (c) => (guides || []).filter((g) => (g.country || "Italy") === c).length;
  const shown = guides && (country ? guides.filter((g) => (g.country || "Italy") === country) : guides);

  useEffect(() => {
    Promise.all([api.get("/guides"), api.get("/stats")])
      .then(([g, s]) => { setGuides(g.data); setStats(s.data); })
      .catch((e) => toast.error(errMsg(e)));
    api.get("/sales").then((r) => setSales(Object.fromEntries(r.data.by_guide.map((x) => [x.guide_id, x])))).catch(() => {});
  }, []);

  return (
    <div className="rise">
      <AssetHealth />
      <div className="flex flex-wrap items-end justify-between gap-6 mb-14">
        <div>
          <p className="eyebrow mb-3">The Expat Studio · Part of AntelliHQ</p>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#3B1B48] leading-none">Small Towns Series</h1>
          <p className="mt-4 text-[#8C827A] max-w-xl">Every guide in the series: where it is in production, its edition and update date, and how it is selling on theexpatstudio.com.</p>
        </div>
        <Button asChild className="rounded-none h-12 px-6" data-testid="dashboard-new-guide-button">
          <Link to="/new"><Plus className="w-4 h-4 mr-2" />New guide</Link>
        </Button>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-8 mb-16">
        <Stat label="Total issues" value={stats.total} testId="stat-total" />
        <Stat label="Published" value={stats.published} testId="stat-published" />
        <Stat label="In progress" value={stats.in_progress} testId="stat-in-progress" />
        <Stat label="Due for update" value={stats.due_for_update} testId="stat-due-for-update" />
        <Stat label="Next issue no." value={stats.next_issue} testId="stat-next-issue"
          sub={Object.entries(stats.next_issue_by_country || {}).map(([c, n]) => `${c} ${n}`).join(" · ")} />
      </div>

      <SalesGoal />

      <UpdateAllGuides />
      <PriceRefresh />
      <MarketSnapshot />
      <MissingShorts />

      <p className="text-sm text-[#8C827A] mb-16 -mt-4" data-testid="dashboard-towns-note">Planning the next issues, the map and progress by region live in <Link to="/towns" className="text-[#3B1B48] underline hover:text-[#C59B27]" data-testid="dashboard-towns-link">Towns</Link>. This page tracks the guides you have made: production, editions and sales.</p>

      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <h2 className="font-display text-2xl text-[#3B1B48]">Guides made</h2>
        <label className="flex items-center gap-3 text-sm text-[#8C827A]">Country
          <select data-testid="guides-country-filter" value={country} onChange={(e) => setCountry(e.target.value)} className="h-10 bg-white border border-input px-3 text-sm text-[#1C181D] rounded-none">
            <option value="">All countries · {guides?.length || 0}</option>
            {countryList.map((c) => <option key={c.name} value={c.name}>{c.name} · {countOf(c.name)}</option>)}
          </select>
        </label>
      </div>
      <div className="bg-[#F4EFE6] border border-[#E2D9CC] overflow-x-auto" data-testid="guides-table">
        <div className="min-w-[880px] grid grid-cols-[70px_1.3fr_1fr_130px_150px_100px_40px] px-6 py-4 border-b border-[#E2D9CC] eyebrow !text-[#8C827A]">
          <span>No.</span><span>Town</span><span>Store title</span><span>Production</span><span>Store</span><span>Sales</span><span />
        </div>
        {guides === null && <p className="p-8 text-[#8C827A]">Loading…</p>}
        {guides?.length === 0 && (
          <div data-testid="guides-empty-state" className="p-14 text-center">
            <p className="font-display text-2xl text-[#3B1B48]">No guides yet</p>
            <p className="text-[#8C827A] mt-2">Type a town name and the studio will do the rest.</p>
          </div>
        )}
        {guides?.length > 0 && shown.length === 0 && <p data-testid="guides-filter-empty" className="p-10 text-[#8C827A]">No {country} guides yet.</p>}
        {shown?.map((g) => (
          <Link key={g.id} to={`/guides/${g.id}`} data-testid={`guide-row-${g.id}`}
            className="min-w-[880px] grid grid-cols-[70px_1.3fr_1fr_130px_150px_100px_40px] items-center px-6 py-5 border-b border-[#E2D9CC] last:border-0 hover:bg-[#EFE6F3]/60 transition-colors group">
            <span className="font-display text-2xl text-[#C59B27]">{g.issue_number}</span>
            <span>
              <span className="font-display text-xl text-[#3B1B48]">{g.town}</span>
              <span className="flex items-center gap-2 text-xs text-[#8C827A] mt-0.5"><CountryTag country={g.country} testId={`guide-country-${g.id}`} />{g.family && <span data-testid={`guide-family-${g.id}`} className="font-mono-accent uppercase text-[10px] tracking-wider px-1.5 py-0.5 bg-[#EFE6F3] text-[#3B1B48]">Families</span>}{g.region}</span>
            </span>
            <span className="text-sm text-[#1C181D] truncate pr-4">{g.listing_title || "—"}</span>
            <span className="flex flex-col items-start gap-1.5">
              <StatusBadge status={g.updating ? "updating" : g.status} testId={`guide-status-${g.id}`} />
              {g.update_due && <span data-testid={`guide-update-due-${g.id}`} className="text-[10px] font-mono-accent uppercase tracking-wider text-[#B45309]">Update due</span>}
            </span>
            <span className="text-xs font-mono-accent uppercase tracking-wider text-[#8C827A]">{g.published ? "Published" : "Draft"}<span className="block normal-case tracking-normal mt-1">Ed. {g.edition}{g.next_update_due ? ` · update ${new Date(g.next_update_due).toLocaleDateString("en-AU", { month: "short", year: "numeric" })}` : ""}</span></span>
            <span className="text-sm text-[#1C181D]" data-testid={`guide-sales-${g.id}`}>{sales[g.id] ? <>{sales[g.id].orders} sold<span className="block text-xs text-[#8C827A]">A${Math.round(sales[g.id].revenue).toLocaleString("en-AU")}</span></> : <span className="text-[#8C827A]">—</span>}</span>
            <ArrowUpRight className="w-5 h-5 text-[#8C827A] group-hover:text-[#3B1B48] transition-colors" />
          </Link>
        ))}
      </div>
      <ReminderSettings />
    </div>
  );
}
